import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { SingularDayCategory } from '@ebalance/proto/bffuser/v1/singular_day_model_pb'

import { usePersistedFilters } from '~/hooks/usePersistedFilters'
import {
  type AdminSingularDayFilters,
  adminSingularDayFiltersCodec,
} from '~/pages/AdminSingularDay/models/persistedFilters'

/**
 * 特異日マスタのフィルタバー保持 key。
 *
 * AC-SC-031 [P0] (`docs/specs/common/shared-components.md`) は「フィルタバーを持つ
 * 全画面において、フィルタの選択状態を localStorage で永続化し、他画面への遷移後・
 * 再訪時に前回の選択状態を復元する」と保存媒体まで規定するため localStorage を使う
 * (タブ生存期間で消える sessionStorage は「再訪時に復元」を満たさない)。
 */
export const ADMIN_SINGULAR_DAY_FILTERS_STORAGE_KEY =
  'ebalance-admin-singular-day-filters'

type UsePersistedSingularDayFiltersResult = {
  fiscalYear: number | undefined
  setFiscalYear: (value: number | undefined) => void
  areaFilter: AreaCode | undefined
  setAreaFilter: (value: AreaCode | undefined) => void
  categoryFilter: SingularDayCategory | undefined
  setCategoryFilter: (value: SingularDayCategory | undefined) => void
}

/**
 * フィルタバーの 3 フィルタ (年度 / エリア / カテゴリ) を localStorage に保持し、
 * 再訪をまたいで復元する hook (AC-SC-031)。直列化・復元・バリデーションは
 * `models/persistedFilters.ts` の純関数が SoT。
 */
export function usePersistedSingularDayFilters(): UsePersistedSingularDayFiltersResult {
  // 読み書きのタイミング (初期復元 / 変更時の書き戻し / 未操作の既定値は書かない /
  // Strict Mode 対策) は共通 hook が持つ。
  const { filters, updateFilters } =
    usePersistedFilters<AdminSingularDayFilters>({
      storageKey: ADMIN_SINGULAR_DAY_FILTERS_STORAGE_KEY,
      codec: adminSingularDayFiltersCodec,
    })

  return {
    fiscalYear: filters.fiscalYear,
    setFiscalYear: (value) =>
      updateFilters((prev) => ({ ...prev, fiscalYear: value })),
    areaFilter: filters.areaFilter,
    setAreaFilter: (value) =>
      updateFilters((prev) => ({ ...prev, areaFilter: value })),
    categoryFilter: filters.categoryFilter,
    setCategoryFilter: (value) =>
      updateFilters((prev) => ({ ...prev, categoryFilter: value })),
  }
}
